import {Trans} from '@lingui/macro';
import {uniq} from 'lodash';
import React, {FC, useState} from 'react';
import {createSelector} from 'reselect';
import styled from 'styled-components';

import {globalWindow} from '../../../../../libs/shared/helpers/src/browser/browserHelpers';
import {BugsnagSeveritiesEnum} from '../../../../../libs/shared/helpers/src/bugsnag/bugsnagSeverities';
import {useInitialEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {ExtendedError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {isNonEmptyString} from '../../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {reportToBugsnag} from '../../../../../srcCommon/services/bugsnag/bugsnag';
import {buildCancelTokenSource, CancelError, CancelTokenSource} from '../../../../helpers/asyncCancelHelpers';
import {delayAsync} from '../../../../helpers/asyncHelpers';
import {useAppDispatch, useAppSelector, wrapActionCreator} from '../../../../helpers/reduxHelpers';
import {IncidentModel} from '../../../../models/api/incidentModel';
import {makeIncidentConnectors} from '../../../../services/incidents';
import {
  IncidentsConnector,
  IncidentsUnavailableError,
} from '../../../../services/incidents/incidentsConnector';
import {cleanVisibleIncidents, filterVisibleIncidents} from '../../../../services/incidents/incidentsHelpers';
import {getUserParticipatingChannels} from '../../../../store/data/channels/channelsSelectors';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {TopBarButton} from '../topBarButton';
import {settingsButtonMargin} from '../topBarHelpers';
import {IncidentsDropdown} from './incidentsDropdown';

/*
 * Constants.
 */

const requestInterval = 2 * 60 * 1000; // Load the data every 2 minutes, alternating between the 2 status pages.

/*
 * Styles.
 */

const StyledTopBarButton = styled(TopBarButton)`
  margin-left: ${settingsButtonMargin}px;
`;

/*
 * Component.
 */

export const Incidents: FC = () => {
  const theme = useUserTheme();
  const [incidents, setIncidents] = useState<ReadonlyArray<IncidentModel>>([]);
  const [partnerIncidents, setPartnerIncidents] = useState<ReadonlyArray<IncidentModel>>([]);

  const ownLabels = useAppSelector(getOwnLabels);

  const dispatch = useAppDispatch();
  const onOpenUrlClick = wrapActionCreator(dispatch, navigateToLinkAction);

  /*
   * Fetch incidents periodically.
   */

  useInitialEffect(() => {
    const {statusPageConnector, partnerStatusPageConnector} = makeIncidentConnectors();
    const cancelTokenSource = buildCancelTokenSource();

    const fetchStatusPageAsync = async (connector: IncidentsConnector) => {
      const newIncidents = await fetchIncidentsAsync(connector, cancelTokenSource, ownLabels);
      setIncidents(newIncidents);

      // Wait a bit, and start the next fetch of partner incidents.
      await delayAsync(requestInterval, cancelTokenSource.token);
      fetchPartnerStatusPageAsync(partnerStatusPageConnector);
    };

    const fetchPartnerStatusPageAsync = async (connector: IncidentsConnector) => {
      const newPartnerIncidents = await fetchIncidentsAsync(connector, cancelTokenSource, ownLabels);
      setPartnerIncidents(newPartnerIncidents);

      // Wait a bit, and start the next fetch of incidents.
      await delayAsync(requestInterval, cancelTokenSource.token);
      fetchStatusPageAsync(statusPageConnector);
    };

    // Fetching main status pages on initial load, then we let them call each other every "requestInterval".
    fetchStatusPageAsync(statusPageConnector);

    return () => cancelTokenSource.cancel();
  });

  /*
   * Render.
   */

  if (partnerIncidents.length === 0 && incidents.length === 0) {
    return null;
  }

  const renderIncidentsButton = (isDropdownOpen: boolean) => (
    <StyledTopBarButton
      iconName="warningFilledTriangle"
      iconColor={theme.palette.orange.shade40}
      isSelected={isDropdownOpen}
      renderTooltip={() => <Trans id="incidents-button">Incidents</Trans>}
    >
      <Trans id="incidents-button">Incidents</Trans>
    </StyledTopBarButton>
  );

  const renderIncidentsDropdown = (popoverProps: PopoverTransferProps) => (
    <IncidentsDropdown
      popoverProps={popoverProps}
      incidents={[...incidents, ...partnerIncidents]}
      onOpenUrlClick={onOpenUrlClick}
    />
  );

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => renderIncidentsButton(isOpen)}
      renderModal={(popoverProps) => renderIncidentsDropdown(popoverProps)}
    />
  );
};

/*
 * Helpers.
 */

const getOwnLabels = createSelector(
  [getCompany, getUserParticipatingChannels],
  (company, participatingChannels) => {
    const accessibleChannelTypes = uniq(participatingChannels.map((channel) => channel.type));

    return [company.region, company.shard, company.id.toString(), ...accessibleChannelTypes]
      .filter(isNonEmptyString)
      .map((label) => label.toLowerCase());
  },
);

function maybeReportFetchStatusPageError(error: any): void {
  // Don't report network errors and such to Bugsnag.
  if (error instanceof IncidentsUnavailableError) {
    return;
  }

  reportToBugsnag(new ExtendedError('Failed to fetch status page.', error), {
    severity: BugsnagSeveritiesEnum.INFO,
  });
}

async function fetchIncidentsAsync(
  connector: IncidentsConnector,
  cancelToken: CancelTokenSource,
  ownLabels: string[],
) {
  try {
    if (globalWindow.navigator.onLine) {
      const newIncidents = await connector.fetchIncidentsAsync(cancelToken.token);

      const visibleIncidents = filterVisibleIncidents(newIncidents, ownLabels);

      return cleanVisibleIncidents(visibleIncidents);
    }
  } catch (error) {
    // Unless the component was unmounted, we maybe report.
    if (!(error instanceof CancelError)) {
      maybeReportFetchStatusPageError(error);
    }
  }

  return [];
}
